// Central knobs for the analytics layer. Everything tunable lives here so we can
// throttle, disable or repoint capture without touching call-sites.
//
// Copied from PUIG's @analytics (rollout PRD §4). The app-specific surface is exactly what this
// file holds — app slug, endpoint, version — plus the identity signals imported in context.js and
// index.js; everything else in the folder is app-agnostic and should stay byte-identical to PUIG
// until the SDK is extracted to a shared package.

// Where the build runs and which code it carries — `testing-ebfed5e`, `prod-<commit>`, or `dev`
// from a dev machine. Composed in build.js / serve.js and replaced here textually by esbuild, so it
// has to be written as a bare reference.
//
// ⚠️ This line used to carry a `typeof process !== "undefined"` guard and that silently broke it:
// esbuild replaces the *key*, not the guard, so the compiled line read
// `typeof process < "u" && process.env && "ebfed5e" || "dev"` — false in a browser, where there is
// no `process` — and every deployed build stamped itself `dev`, indistinguishable from a laptop.
// Measured on testing, 2026-09-04. The try/catch is the safety net the guard was meant to be: it
// survives a build that forgets the define without discarding the value when it is there.
let appVersion = "dev"
try {
    appVersion = process.env.APP_VERSION || "dev"
} catch { /* no define in this build — stay "dev" */ }

// The environment on its own — `prod`, `testing` or `dev` — same label `appVersion` carries and
// same source (build.js / serve.js), so the two cannot disagree. Bare references for the same
// reason as above. A build with no define is `dev`, which means: no capture.
let appEnv = "dev"
try {
    appEnv = process.env.APP_ENV || "dev"
} catch { /* no define — stay "dev" */ }

// Explicit override for checking an event on a dev machine or on testing without deploying to
// production. Off unless the build was started with `APP_ANALYTICS=on`; the pipeline never sets it.
let analyticsForced = false
try {
    analyticsForced = process.env.APP_ANALYTICS === "on"
} catch { /* no define — not forced */ }

export const config = {
    // Stable app slug, rides on every envelope (rollout PRD §3.1). Rows stay self-describing if the
    // data is ever consolidated across apps.
    app: "dipi",

    // Kill-switch: when false nothing runs — no init, no listeners, no network. Doubles as the
    // consent gate if one is ever needed.
    //
    // Capture is **production only**, by decision (2026-09-09): testing and dev machines emit
    // nothing, so the events table holds real usage and nothing else. `APP_ANALYTICS=on` at build
    // time is the one way to switch it on elsewhere, and it has to be asked for by name.
    enabled: appEnv === "prod" || analyticsForced,

    // Ingest endpoint. Same-origin relative path → the v1 session cookie rides along
    // automatically (same as the rest of the app's /api/v2 calls). dipi-webback-v2 stores the
    // batch in `dip-insights`.DIPI_PRODUCT_EVENTS.
    endpoint: "/api/v2/events",

    // Batching: flush whichever comes first.
    batchSize: 20,          // events
    flushIntervalMs: 5000,  // ms

    // Bounded queue — analytics is best-effort. If the API is unreachable we drop the
    // oldest events beyond this cap instead of growing memory without limit.
    maxQueueSize: 200,

    // Session: a new session starts after this much inactivity, or when the user changes.
    sessionTimeoutMs: 30 * 60 * 1000, // 30 min

    // Engaged-time: pause the on-screen timer after this long with no interaction (AFK).
    idleTimeoutMs: 60 * 1000, // 60 s

    // Screen debounce: a route must "stick" this long before it counts as a real
    // screen_view. Filters out redirect chains (logout → login → / → home) that fire
    // several route changes in milliseconds. Dipi's is longer than PUIG's 400: every page load
    // passes through /login while /api/session/check-login answers (~1 s measured 2026-09-03),
    // and 400 ms recorded a phantom "login" screen on each reload.
    screenDebounceMs: 1200,

    // App version, injected at build time (see build.js / serve.js define block).
    appVersion,

    // Console diagnostics in dev only.
    debug: process.env.NODE_ENV !== "production",
}
